import { Box, Container, Grid, Typography, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';

const ProcessWalkthrough = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = [
    {
      title: "1. Choose Your License",
      desc: "Select the Windows, MS Office, or Windows Server license key that meets your requirements.",
      icon: "🔑"
    },
    {
      title: "2. Secure Checkout",
      desc: "Complete your transaction through our secure encrypted gateway using UPI, cards, or Net Banking.",
      icon: "💳"
    },
    {
      title: "3. Instant E-Delivery",
      desc: "Get your genuine software activation key and official download link instantly in your email inbox.",
      icon: "⚡"
    },
    {
      title: "4. Quick Activation",
      desc: "Follow our simple step-by-step activation guide to activate your software in less than 5 minutes.",
      icon: "🚀"
    }
  ];

  return (
    <Box sx={{ py: { xs: 3, md: 6 }, bgcolor: '#ffffff' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 800,
            mb: 2,
            fontSize: { xs: '1.8rem', md: '3rem' }
          }}
        >
          How It Works: Secure Activation Process
        </Typography>
        <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: { xs: 4, md: 6 }, maxWidth: 700, mx: 'auto', px: { xs: 2, md: 0 } }}>
          Experience a seamless transition to genuine licensed software with our secure setup.
        </Typography>

        <Grid container spacing={{ xs: 2, md: 4 }} alignItems="stretch">
          {steps.map((step, index) => (
            <Grid item xs={12} sm={6} md={3} key={index} sx={{ display: 'flex' }}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                style={{ width: '100%', display: 'flex' }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    textAlign: 'center',
                    p: { xs: 3, md: 4 },
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: '#e6eff8ff',
                    borderRadius: 2,
                    border: '1px solid #EAEAEA',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.05)',
                      transform: 'translateY(-4px)'
                    },
                    transition: 'all 0.3s'
                  }}
                >
                  <Typography variant="h1" sx={{ fontSize: { xs: '3rem', md: '3.6rem' }, mb: 2 }}>
                    {step.icon}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main', fontSize: { xs: '1.05rem', md: '1.2rem' } }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, flexGrow: 1 }}>
                    {step.desc}
                  </Typography>

                  {/* Connector Arrow for Desktop */}
                  {index < 3 && (
                    <Box
                      sx={{
                        display: { xs: 'none', md: 'block' },
                        position: 'absolute',
                        top: '50%',
                        right: -24,
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        color: '#0E6ADA',
                        fontWeight: 700
                      }}
                    >
                      <Typography variant="h5">→</Typography>
                    </Box>
                  )}
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Traditional USP Badge */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          sx={{
            mt: { xs: 6, md: 10 },
            p: { xs: 3, md: 4 },
            textAlign: 'center',
            border: '2px solid #0e6ada',
            bgcolor: 'rgba(14, 106, 218, 0.05)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0e6ada', fontSize: { xs: '1.1rem', md: '1.5rem' } }}>
            "Genuine product keys with lifetime validity and official updates."
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.8, fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
            All products are 100% genuine digital licenses and receive automatic security and stability patches directly from Microsoft server portals.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ProcessWalkthrough;
